import { observer } from 'mobx-react-lite'
import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Box, Text, Button, TextInput } from '@mantine/core'
import { TagFilterChips, AuthGateCta } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import RegistryList from './RegistryList'
import { TagCategory } from '../../app/models/tag'

const PLATFORM_CHIPS = ['All', 'AR-15', 'AK-Pattern', 'Pistol', 'Glock', 'Bolt Action', 'Shotgun', 'Revolver', 'Other']
const SORT_OPTIONS = [
  { value: '', label: 'Latest' },
  { value: 'manufacturer', label: 'Manufacturer' }
]

const chipBase = {
  background: '#21262D',
  border: '1px solid var(--gs-border)',
  color: 'var(--gs-muted)',
  padding: '5px 11px',
  borderRadius: 5,
  fontSize: 12,
  fontFamily: 'DM Sans, sans-serif',
  cursor: 'pointer',
  transition: 'border-color 0.15s, color 0.15s, background 0.15s'
}
const chipActive = {
  borderColor: 'var(--gs-accent)',
  color: 'var(--gs-accent)',
  background: 'rgba(232,93,4,0.1)'
}

const INPUT_STYLES = {
  input: {
    background: 'var(--gs-surface)',
    border: '1.5px solid var(--gs-border)',
    color: 'var(--gs-text)',
    fontSize: 12,
    fontFamily: 'DM Sans, sans-serif',
    '&:focus': { borderColor: 'var(--gs-accent)' }
  }
}

export default observer(function RegistryDashboard () {
  const { firearmModelStore, userStore } = useStore()
  const {
    loadModels,
    setFilters,
    filterMake,
    filterPlatform,
    filterCaliber,
    filterSortBy,
    filterTagIds,
    totalCount
  } = firearmModelStore

  const [makeInput, setMakeInput] = useState(filterMake)
  const [caliberInput, setCaliberInput] = useState(filterCaliber)

  const applySearch = useCallback(() => {
    setFilters(makeInput.trim() || undefined, filterPlatform, caliberInput.trim() || undefined, filterTagIds, 'any', filterSortBy || undefined)
    loadModels()
  }, [makeInput, caliberInput, filterPlatform, filterTagIds, filterSortBy, setFilters, loadModels])

  useEffect(() => {
    loadModels()
  }, [loadModels])

  useEffect(() => {
    const t = setTimeout(applySearch, 300)
    return () => clearTimeout(t)
  }, [makeInput, caliberInput]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlatform = (value: string) => {
    const platform = value === 'All' ? '' : value
    setFilters(filterMake, platform, filterCaliber, filterTagIds, 'any', filterSortBy || undefined)
    loadModels()
  }

  const handleSort = (value: string) => {
    setFilters(filterMake, filterPlatform, filterCaliber, filterTagIds, 'any', value || undefined)
    loadModels()
  }

  const handleTagToggle = (tagId: string) => {
    const next = filterTagIds.includes(tagId)
      ? filterTagIds.filter((id) => id !== tagId)
      : [...filterTagIds, tagId]
    setFilters(filterMake, filterPlatform, filterCaliber, next, 'any', filterSortBy || undefined)
    loadModels()
  }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100%' }}>
      <Box
        sx={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--gs-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <Box>
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 20, color: 'var(--gs-text)', margin: 0 }}>
            FIREARM REGISTRY
          </Text>
          <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)', marginTop: 2 }}>
            {totalCount} community-verified models
          </Text>
        </Box>
        <Button component={Link} to="/registry/new" sx={{ background: 'var(--gs-accent)', '&:hover': { background: '#FF6A1A' } }}>
          + Submit a Model
        </Button>
      </Box>

      {!userStore.isLoggedIn && (
        <Box sx={{ padding: '12px 24px 0' }}>
          <AuthGateCta
            variant="banner"
            action="add firearms to your Vault and submit models"
            pitch="Register free to track your collection, submit new models, and earn AmmoScore as a community contributor."
          />
        </Box>
      )}

      <Box
        sx={{
          padding: '10px 24px',
          borderBottom: '1px solid var(--gs-border)',
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
          }
        }}
      >
        <Box sx={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {PLATFORM_CHIPS.map((value) => {
            const isActive = value === 'All' ? !filterPlatform : filterPlatform === value
            return (
              <Box key={value} component="button" type="button" onClick={() => handlePlatform(value)}
                sx={{ ...chipBase, ...(isActive ? chipActive : {}), flexShrink: 0 }}>
                {value}
              </Box>
            )
          })}

          <Box sx={{ width: 1, height: 18, background: 'var(--gs-border)', marginX: 2, flexShrink: 0, '@media (max-width: 768px)': { width: '100%' } }} />

          {SORT_OPTIONS.map((opt) => (
            <Box key={opt.value} component="button" type="button" onClick={() => handleSort(opt.value)}
              sx={{ ...chipBase, ...(filterSortBy === opt.value ? chipActive : {}), flexShrink: 0 }}>
              {opt.label}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', '@media (max-width: 768px)': { width: '100%' } }}>
          <TextInput
            placeholder="Caliber…"
            value={caliberInput}
            onChange={(e) => setCaliberInput(e.currentTarget.value)}
            rightSection={caliberInput
              ? (
              <Box component="button" type="button" onClick={() => setCaliberInput('')}
                sx={{ background: 'none', border: 'none', color: 'var(--gs-muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>
                ×
              </Box>
                )
              : null}
            sx={{ flex: '1 1 120px', minWidth: '100px', '@media (max-width: 768px)': { flex: '1 1 100%' } }}
            styles={INPUT_STYLES}
          />
          <TextInput
            placeholder="Make or model…"
            value={makeInput}
            onChange={(e) => setMakeInput(e.currentTarget.value)}
            rightSection={makeInput
              ? (
              <Box component="button" type="button" onClick={() => setMakeInput('')}
                sx={{ background: 'none', border: 'none', color: 'var(--gs-muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>
                ×
              </Box>
                )
              : null}
            sx={{ flex: '1 1 168px', minWidth: '100px', '@media (max-width: 768px)': { flex: '1 1 100%' } }}
            styles={INPUT_STYLES}
          />
        </Box>
      </Box>

      <Box sx={{ padding: '8px 24px', borderBottom: '1px solid var(--gs-border)' }}>
        <TagFilterChips
          selectedTagIds={filterTagIds}
          onToggleTag={handleTagToggle}
          categories={[TagCategory.Platform, TagCategory.Caliber]}
        />
      </Box>

      <RegistryList />
    </Box>
  )
})
